import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, Users, MessageSquare, Crown, Zap, Shield, ArrowUpCircle, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TopupDialog } from '@/components/TopupDialog';
import { 
  TIER_LIMITS, 
  TIER_NAMES, 
  TIER_PRICING, 
  SubscriptionTier, 
  formatCurrency,
  getTierLimits,
} from '@/lib/subscriptionFeatures';
import { format } from 'date-fns';

interface CompanySubscription {
  id: string;
  company_name: string;
  subscription_tier: string;
  ai_pool_limit: number;
  ai_pool_used: number;
  topup_conversations: number;
  max_sites: number;
  site_count: number;
  worker_count: number;
  upgrade_requested_tier: string | null;
  upgrade_requested_at: string | null;
  topup_requested_amount: number | null;
  topup_requested_at: string | null;
}

const AdminSubscription = () => {
  const [companies, setCompanies] = useState<CompanySubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [isTopupDialogOpen, setIsTopupDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanySubscription | null>(null);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>('professional');
  const { toast } = useToast();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      const { data: companiesData, error } = await supabase
        .from('companies')
        .select('*')
        .order('company_name', { ascending: true });

      if (error) throw error;

      const companiesWithCounts = await Promise.all(
        (companiesData || []).map(async (company) => {
          const { count: siteCount } = await supabase
            .from('company_locations')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', company.id)
            .eq('is_active', true);
          
          const { count: workerCount } = await supabase
            .from('worker_profiles')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', company.id);
          
          return { 
            ...company, 
            site_count: siteCount || 0,
            worker_count: workerCount || 0,
          };
        })
      );

      setCompanies(companiesWithCounts);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradeCompany = async () => {
    if (!selectedCompany) return;
    setIsLoading(true);

    try {
      const tierLimits = getTierLimits(selectedTier);

      // Update companies table
      const { error: companyError } = await supabase
        .from('companies')
        .update({
          subscription_tier: selectedTier,
          ai_pool_limit: tierLimits.aiConversationsPerMonth,
          max_sites: tierLimits.maxSites,
        })
        .eq('id', selectedCompany.id);

      if (companyError) throw companyError;

      // Also update company_settings to keep in sync
      const { error: settingsError } = await supabase
        .from('company_settings')
        .update({
          subscription_level: selectedTier,
        })
        .eq('company_id', selectedCompany.id);

      if (settingsError) {
        console.error('Failed to sync company_settings:', settingsError);
      }

      toast({
        title: "Success",
        description: `${selectedCompany.company_name} upgraded to ${TIER_NAMES[selectedTier]}`,
      });

      setIsUpgradeDialogOpen(false);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  // Approve upgrade request using RPC
  const handleApproveUpgrade = async (companyId: string) => {
    try {
      const { data, error } = await supabase.rpc('approve_upgrade_request', { p_company_id: companyId });
      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; new_tier?: string };
      if (!result.success) {
        toast({ title: "Error", description: result.error || "Failed to approve", variant: "destructive" });
        return;
      }

      toast({
        title: "Upgrade Approved",
        description: `Company upgraded to ${TIER_NAMES[result.new_tier as SubscriptionTier] || result.new_tier}`,
      });
      fetchCompanies();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  // Reject upgrade request using RPC
  const handleRejectUpgrade = async (companyId: string) => {
    try {
      const { data, error } = await supabase.rpc('reject_upgrade_request', { p_company_id: companyId });
      if (error) throw error;
      
      toast({ title: "Request Rejected", description: "Upgrade request has been cleared." });
      fetchCompanies();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  // Approve topup request using RPC
  const handleApproveTopup = async (companyId: string) => {
    try {
      const { data, error } = await supabase.rpc('approve_topup_request', { p_company_id: companyId });
      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; amount?: number };
      if (!result.success) {
        toast({ title: "Error", description: result.error || "Failed to approve", variant: "destructive" });
        return;
      }

      toast({
        title: "Top-up Approved",
        description: `Added ${result.amount} conversations to company pool`,
      });
      fetchCompanies();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  // Reject topup request using RPC
  const handleRejectTopup = async (companyId: string) => {
    try {
      const { data, error } = await supabase.rpc('reject_topup_request', { p_company_id: companyId });
      if (error) throw error;
      
      toast({ title: "Request Rejected", description: "Top-up request has been cleared." });
      fetchCompanies();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'professional':
        return <Badge className="bg-primary text-primary-foreground"><Zap className="h-3 w-3 mr-1" />Professional</Badge>;
      case 'enterprise':
        return <Badge className="bg-success text-success-foreground"><Shield className="h-3 w-3 mr-1" />Enterprise</Badge>;
      case 'enterprise_plus':
        return <Badge className="bg-warning text-warning-foreground"><Crown className="h-3 w-3 mr-1" />Enterprise+</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const totalWorkers = companies.reduce((sum, c) => sum + (c.worker_count || 0), 0);
  const totalAIUsed = companies.reduce((sum, c) => sum + (c.ai_pool_used || 0), 0);
  const totalSites = companies.reduce((sum, c) => sum + (c.site_count || 0), 0);

  // Filter pending requests
  const pendingUpgrades = companies.filter(c => c.upgrade_requested_tier);
  const pendingTopups = companies.filter(c => c.topup_requested_amount);
  const totalPending = pendingUpgrades.length + pendingTopups.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Subscription Management</h2>
        <p className="text-muted-foreground">Manage company subscriptions (company-level AI pooling)</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Companies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{companies.length}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Sites</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold">{totalSites}</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Workers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{totalWorkers}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">AI Conversations (Month)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{totalAIUsed.toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests Section */}
      {totalPending > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Pending Requests ({totalPending})
            </CardTitle>
            <CardDescription>Upgrade and top-up requests awaiting approval</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Request Type</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUpgrades.map((company) => (
                  <TableRow key={`upgrade-${company.id}`}>
                    <TableCell className="font-medium">{company.company_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-primary text-primary">
                        <ArrowUpCircle className="h-3 w-3 mr-1" />
                        Upgrade
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTierBadge(company.subscription_tier)}
                        <span className="text-muted-foreground">→</span>
                        {getTierBadge(company.upgrade_requested_tier!)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {company.upgrade_requested_at 
                        ? format(new Date(company.upgrade_requested_at), 'dd MMM yyyy HH:mm')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleApproveUpgrade(company.id)}
                          className="bg-success hover:bg-success/90"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleRejectUpgrade(company.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {pendingTopups.map((company) => (
                  <TableRow key={`topup-${company.id}`}>
                    <TableCell className="font-medium">{company.company_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-amber-500 text-amber-600">
                        <Zap className="h-3 w-3 mr-1" />
                        Top-up
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{company.topup_requested_amount} conversations</span>
                      <span className="text-muted-foreground ml-2">
                        ({formatCurrency(company.topup_requested_amount! * 2000)})
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {company.topup_requested_at 
                        ? format(new Date(company.topup_requested_at), 'dd MMM yyyy HH:mm')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleApproveTopup(company.id)}
                          className="bg-success hover:bg-success/90"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleRejectTopup(company.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="companies">
        <TabsList>
          <TabsTrigger value="companies">Company Subscriptions</TabsTrigger>
          <TabsTrigger value="plans">Plan Comparison</TabsTrigger>
        </TabsList>

        <TabsContent value="companies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Company Subscriptions</CardTitle>
              <CardDescription>AI conversation pool is managed at company level (shared across all sites)</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Sites</TableHead>
                    <TableHead>Workers</TableHead>
                    <TableHead>AI Pool Usage</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                    </TableRow>
                  ) : companies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No companies found</TableCell>
                    </TableRow>
                  ) : (
                    companies.map((company) => {
                      const totalAI = (company.ai_pool_used || 0);
                      const limit = company.ai_pool_limit || 100;
                      const topup = company.topup_conversations || 0;
                      const aiPercentage = limit === -1 ? 0 : (totalAI / limit) * 100;
                      const isAINearLimit = aiPercentage >= 80;
                      const hasPendingRequest = company.upgrade_requested_tier || company.topup_requested_amount;

                      return (
                        <TableRow key={company.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{company.company_name}</p>
                              {hasPendingRequest && (
                                <Badge variant="outline" className="mt-1 text-xs border-amber-500 text-amber-600">
                                  <Clock className="h-2 w-2 mr-1" />
                                  Pending Request
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{getTierBadge(company.subscription_tier)}</TableCell>
                          <TableCell>
                            {company.site_count} / {company.max_sites === -1 ? '∞' : company.max_sites}
                          </TableCell>
                          <TableCell>{company.worker_count} (unlimited)</TableCell>
                          <TableCell>
                            <div className="space-y-1 min-w-[150px]">
                              {limit === -1 ? (
                                <span className="text-sm text-muted-foreground">Unlimited</span>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between text-sm">
                                    <span>{totalAI} / {limit}{topup > 0 ? ` (+${topup})` : ''}</span>
                                    {isAINearLimit && <AlertTriangle className="h-3 w-3 text-warning" />}
                                  </div>
                                  <Progress value={aiPercentage} className={`h-1.5 ${isAINearLimit ? '[&>div]:bg-warning' : ''}`} />
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => {
                                setSelectedCompany(company);
                                setSelectedTier(company.subscription_tier === 'free' ? 'professional' : 'enterprise');
                                setIsUpgradeDialogOpen(true);
                              }}>
                                <ArrowUpCircle className="h-3 w-3 mr-1" />
                                Change
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => {
                                setSelectedCompany(company);
                                setIsTopupDialogOpen(true);
                              }}>
                                <Zap className="h-3 w-3 mr-1" />
                                Top-up
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plans">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {(['free', 'professional', 'enterprise', 'enterprise_plus'] as SubscriptionTier[]).map((tier) => {
              const limits = TIER_LIMITS[tier];
              const pricing = TIER_PRICING[tier];
              
              return (
                <Card key={tier} className={tier === 'professional' ? 'border-primary' : ''}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>{TIER_NAMES[tier]}</CardTitle>
                      {tier === 'professional' && <Badge variant="secondary">Popular</Badge>}
                    </div>
                    <CardDescription>
                      {pricing.monthly === 0 ? (tier === 'enterprise_plus' ? 'Custom' : 'Free') : `${formatCurrency(pricing.monthly)}/month`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Sites</span>
                        <span className="font-medium">{limits.maxSites === -1 ? 'Unlimited' : limits.maxSites}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Workers</span>
                        <span className="font-medium">Unlimited</span>
                      </div>
                      <div className="flex justify-between">
                        <span>AI Conversations/mo</span>
                        <span className="font-medium">{limits.aiConversationsPerMonth === -1 ? 'Unlimited' : limits.aiConversationsPerMonth.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Documents</span>
                        <span className="font-medium">{limits.knowledgeBaseDocs === -1 ? 'Unlimited' : limits.knowledgeBaseDocs}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Upgrade Dialog */}
      <Dialog open={isUpgradeDialogOpen} onOpenChange={setIsUpgradeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription Tier</DialogTitle>
            <DialogDescription>
              Update subscription for {selectedCompany?.company_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Tier</Label>
              <p className="text-sm text-muted-foreground">{TIER_NAMES[selectedCompany?.subscription_tier as SubscriptionTier] || 'Free'}</p>
            </div>
            <div className="space-y-2">
              <Label>New Tier</Label>
              <Select value={selectedTier} onValueChange={(v) => setSelectedTier(v as SubscriptionTier)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free - Rp 0/bulan</SelectItem>
                  <SelectItem value="professional">Professional - {formatCurrency(18000000)}/mo</SelectItem>
                  <SelectItem value="enterprise">Enterprise - {formatCurrency(35000000)}/mo</SelectItem>
                  <SelectItem value="enterprise_plus">Enterprise+ - Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUpgradeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpgradeCompany} disabled={isLoading}>Confirm Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top-up Dialog */}
      {selectedCompany && (
        <TopupDialog
          open={isTopupDialogOpen}
          onOpenChange={setIsTopupDialogOpen}
          companyId={selectedCompany.id}
          currentTier={selectedCompany.subscription_tier}
          topupCountThisMonth={0}
          maxTopupsThisMonth={selectedCompany.subscription_tier === 'free' ? 3 : -1}
          onSuccess={fetchCompanies}
        />
      )}
    </div>
  );
};

export default AdminSubscription;
